import React from 'react';
import { X } from 'lucide-react';
import { cellKey, MAX_RENDER_ROWS, groupPendingRows } from '../dbGridModel';
import { dateColumnKind, DateEditButton } from '../dbGridCells';
import DbEnumSelect from './DbEnumSelect';

/** Visibility, selection and row / pending-row renderers for the results grid. Plain function: it runs during render, after the early returns, and holds no hooks. */
export function gridRowRendering({
  result,
  editable,
  pendingRows,
  onUpdatePendingCell,
  onRemovePendingRow,
  selectedRows,
  onToggleRowSelect,
  selectedCells,
  onCellSelect,
  onDatePick,
  columnTypes,
  enumByColumn,
  rowSelectable,
  hiddenColumns,
  windowed,
  virtualRows,
}) {
  // colCount drives colSpan, so it counts what is drawn rather than what
  // exists — a full-width row must not stretch past the visible columns.
  const isHidden = (col) => hiddenColumns.has(col);
  const colCount = result.columns.reduce((n, c) => (isHidden(c) ? n : n + 1), 0);
  // "Last column" for the pending row's discard button means last *visible*,
  // or the button disappears whenever the rightmost column is hidden.
  let lastVisibleIndex = -1;
  result.columns.forEach((c, i) => {
    if (!isHidden(c)) lastVisibleIndex = i;
  });
  const canSelect = (editable || rowSelectable) && typeof onToggleRowSelect === 'function';
  // Selection happens on mousedown so it lands before focus moves. Ctrl/Shift
  // clicks suppress the default so they neither start a native text-selection
  // drag nor drop the caret into a pending row's input mid-range-select; a
  // plain click keeps the default, so clicking a pending cell still focuses
  // its input for typing.
  const cellMouseDown = (e, kind, id, colIndex) => {
    if (!onCellSelect) return;
    // A right-click inside an existing selection must LEAVE it alone. mousedown
    // fires for button 2 as well, so without this, right-clicking one of five
    // selected rows collapsed the selection to that row — and the context menu
    // that opened a moment later then acted on one row instead of five.
    // Right-clicking OUTSIDE the selection still moves it, which is what every
    // file manager does.
    if (e.button === 2) {
      const insideSelection =
        selectedRows?.has(id) || selectedCells?.has(cellKey(kind, id, colIndex));
      if (insideSelection) return;
    }
    if (e.ctrlKey || e.metaKey || e.shiftKey) e.preventDefault();
    onCellSelect(e, kind, id, colIndex);
  };
  const cellClass = (isEdited, isEditing, isSelected) =>
    `dbm-td${isEdited && !isEditing ? ' dbm-td-edited' : ''}${isSelected ? ' dbm-td--cell-selected' : ''}`;
  const totalResultRows = result.rows.length;
  const rowsToRender =
    totalResultRows > MAX_RENDER_ROWS ? result.rows.slice(0, MAX_RENDER_ROWS) : result.rows;

  /**
   * What the tbody actually maps over: `{ row, index }` where **index is the
   * ABSOLUTE position in result.rows**, never a position within the window.
   *
   * That distinction is the whole risk of this change. Every consumer below —
   * `selectedRows.has(index)`, `onToggleRowSelect(index)`, the `${index}:${j}`
   * pendingEdits key, `editingCell.rowIndex`, `cellKey('r', index, j)` for find
   * and cell selection, `onCellOpen`/`onCellContextMenu`/`onDatePick`/
   * `onFkNavigate` — already treats the number it is given as absolute, and so
   * does every handler in DbManagerResultsTab that receives it. Windowing does
   * not change any of them; it only changes where the number comes from. Hand
   * a window-local index to any one of them and it silently edits, selects or
   * highlights the wrong row.
   */
  const renderRows = windowed
    ? virtualRows.map((vr) => ({ row: rowsToRender[vr.index], index: vr.index }))
    : rowsToRender.map((row, i) => ({ row, index: i }));

  /**
   * One pending row, drawn either directly under the row it duplicates or in
   * the block at the bottom — see groupPendingRows.
   *
   * A function rather than two copies of the markup, because the two placements
   * differ in nothing but position, and the earlier single-site version was
   * already 45 lines of inputs, discard buttons and date affordances.
   */
  const { under: pendingUnderRow, trailing: trailingPending } = groupPendingRows(pendingRows);
  const renderPendingRow = (pRow) => (
    <tr key={pRow.id} className="dbm-tr dbm-pending-tr">
      <td className="dbm-td dbm-td-rownum" />
      {pRow.values.map((val, j) => {
        if (isHidden(result.columns[j])) return null;
        const dtype = onDatePick ? dateColumnKind(columnTypes?.[result.columns[j]]) : null;
        const isLast = j === lastVisibleIndex;
        return (
          <td
            key={j}
            className={`${cellClass(false, false, selectedCells?.has(cellKey('p', pRow.id, j)))}${
              isLast ? ' dbm-pending-td-last' : ''
            }${dtype ? ' dbm-td--date' : ''}`}
            data-cell-key={cellKey('p', pRow.id, j)}
            onMouseDown={(e) => cellMouseDown(e, 'p', pRow.id, j)}
          >
            {enumByColumn?.get(result.columns[j]) ? (
              <DbEnumSelect
                meta={enumByColumn.get(result.columns[j])}
                columnName={result.columns[j]}
                blankIsNull
                // A pending row carries NULL as '' (its inputs' "NULL" placeholder).
                value={val === '' ? null : val}
                onChange={(v) => onUpdatePendingCell(pRow.id, j, v ?? '')}
              />
            ) : (
            <input
              type="text"
              // size={1} zeroes the input's intrinsic width (default is ~20
              // chars via the size attribute — CSS width:100% does not override
              // it in auto table layout), so a new pending row can't widen
              // existing columns; the input just fills whatever width each
              // column already has.
              size={1}
              className="dbm-pending-input"
              value={val}
              placeholder="NULL"
              onChange={(e) => onUpdatePendingCell(pRow.id, j, e.target.value)}
            />
            )}
            {dtype && (
              <DateEditButton kind={dtype} onOpen={(rect) => onDatePick('pending', pRow.id, j, rect)} />
            )}
            {isLast && (
              <button
                type="button"
                className="dbm-remove-pending-btn"
                title="Discard row"
                onClick={() => onRemovePendingRow(pRow.id)}
              >
                <X size={12} />
              </button>
            )}
          </td>
        );
      })}
    </tr>
  );

  return {
    isHidden,
    colCount,
    canSelect,
    cellMouseDown,
    cellClass,
    totalResultRows,
    renderRows,
    pendingUnderRow,
    trailingPending,
    renderPendingRow,
  };
}
